"use client";

import { Field as FieldPrimitive } from "@base-ui/react/field";
import type React from "react";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";

export function Field({
  className,
  ...props
}: FieldPrimitive.Root.Props): React.ReactElement {
  return (
    <FieldPrimitive.Root
      className={cn("flex flex-col items-start gap-2", className)}
      data-slot="field"
      {...props}
    />
  );
}

export function FieldLabel({
  className,
  required = false,
  children,
  ...props
}: FieldPrimitive.Label.Props & Omittable<{
  /** Marks the answer as one the form will not accept empty: an asterisk
   *  2px after the label's last character, in the ink the FieldError line
   *  uses. It hugs the label because the eye reads a form down the label
   *  column and a mark that floats off its word reads as a stray glyph. The
   *  glyph is hidden from assistive tech; the label speaks "required"
   *  instead. Marking is the form's call — a form where nearly every answer
   *  is required says nothing here and marks the exceptions instead. */
  required?: boolean;
}>): React.ReactElement {
  return (
    <FieldPrimitive.Label
      className={cn(
        // The quiet label rung: one size and one ink below the value it
        // frames, so the control's content stays the loudest thing in the
        // form. (`Label`, the inline checkbox/radio wrapper, keeps body
        // metrics — its text is the control's own value, not a caption.)
        // An invalid Field lifts the label to the destructive ink alongside
        // its control's border and its FieldError line.
        "inline-flex items-center gap-2 font-medium text-xs/4 text-muted-foreground data-disabled:opacity-64 data-invalid:text-destructive-foreground",
        className,
      )}
      data-slot="field-label"
      {...props}
    >
      {children}
      {required ? (
        // The label's 8px gap is for a glyph beside a word; the mark pulls
        // back through it to sit 2px off the last letter.
        <span
          aria-hidden
          className="-ms-1.5 text-destructive-foreground"
          data-slot="field-required"
        >
          *
        </span>
      ) : null}
      {required ? <span className="sr-only"> (required)</span> : null}
    </FieldPrimitive.Label>
  );
}

export function FieldItem({
  className,
  ...props
}: FieldPrimitive.Item.Props): React.ReactElement {
  return (
    <FieldPrimitive.Item
      className={cn("flex", className)}
      data-slot="field-item"
      {...props}
    />
  );
}

export function FieldDescription({
  className,
  ...props
}: FieldPrimitive.Description.Props): React.ReactElement {
  return (
    <FieldPrimitive.Description
      className={cn("text-muted-foreground text-xs", className)}
      data-slot="field-description"
      {...props}
    />
  );
}

export function FieldError({
  className,
  ...props
}: FieldPrimitive.Error.Props): React.ReactElement {
  return (
    <FieldPrimitive.Error
      className={cn("text-destructive-foreground text-xs", className)}
      data-slot="field-error"
      {...props}
    />
  );
}

export const FieldControl: typeof FieldPrimitive.Control =
  FieldPrimitive.Control;
export const FieldValidity: typeof FieldPrimitive.Validity =
  FieldPrimitive.Validity;
